// The AC Analyst surface — A5.
//
// Two-panel layout per design doc §4.1:
//   LEFT  → dialogue + input box (the conversation)
//   RIGHT → artifact stack (Frames, narrations, simulations, …)
//
// Followup chips on artifacts post their text back into the dialogue
// as the next user message — closing the discovery loop without the
// user having to retype.
//
// Session bootstrap: the user enters installation_id (+ optional
// AC name) and clicks "Open session". Subsequent traffic uses the
// returned session_id.

import { useCallback, useMemo, useRef, useState } from "react";
import { ApiError } from "../api/client";
import {
  AnalystApiClient,
  type ArtifactView,
  type CreateSessionResponse,
} from "../api/analystClient";
import { ArtifactCard } from "./ArtifactCard";

// ─────────────────────────────────────────────────────────────────────
// Dialogue message model (UI-local, not server-side)
// ─────────────────────────────────────────────────────────────────────


type DialogueMessage =
  | { role: "user"; text: string }
  | {
      role: "assistant";
      text: string;
      turns: number;
      budgetExceeded: boolean;
      stuckWarning: boolean;
      artifactIds: string[];
    }
  | { role: "system"; text: string };


export interface AnalystPageProps {
  /** Allow tests to inject a stub client. Defaults to a fresh
   *  ``AnalystApiClient`` against the dev proxy. */
  client?: AnalystApiClient;
}


export function AnalystPage({ client }: AnalystPageProps) {
  const apiClient = useMemo(() => client ?? new AnalystApiClient(), [client]);

  const [installationId, setInstallationId] = useState("retail-demo");
  const [acName, setAcName] = useState("");
  const [session, setSession] = useState<CreateSessionResponse | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);

  const [dialogue, setDialogue] = useState<DialogueMessage[]>([]);
  const [artifacts, setArtifacts] = useState<ArtifactView[]>([]);
  const [inputText, setInputText] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  // ── Open session ──────────────────────────────────────────────

  const handleOpen = useCallback(async () => {
    if (!installationId.trim()) {
      setOpenError("installation_id is required");
      return;
    }
    setOpening(true);
    setOpenError(null);
    try {
      const resp = await apiClient.createSession({
        installation_id: installationId.trim(),
        ac_name: acName.trim() || undefined,
      });
      setSession(resp);
      setDialogue([
        {
          role: "system",
          text: `Session opened against AC '${resp.ac_name}' (model: ${resp.model}, prompt: ${resp.prompt_version}). Ask the analyst anything about your data.`,
        },
      ]);
      setArtifacts([]);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `${err.status}: ${err.message}`
          : err instanceof Error
            ? err.message
            : String(err);
      setOpenError(msg);
    } finally {
      setOpening(false);
    }
  }, [installationId, acName, apiClient]);

  // ── Send message ──────────────────────────────────────────────

  const handleSend = useCallback(
    async (text: string) => {
      if (!session) return;
      const trimmed = text.trim();
      if (!trimmed) return;

      setDialogue((d) => [...d, { role: "user", text: trimmed }]);
      setInputText("");
      setSending(true);
      setSendError(null);
      try {
        const resp = await apiClient.sendMessage(session.session_id, trimmed);
        // Append the assistant's response + accumulate any new artifacts.
        setDialogue((d) => [
          ...d,
          {
            role: "assistant",
            text: resp.text,
            turns: resp.turns_used,
            budgetExceeded: resp.budget_exceeded,
            stuckWarning: resp.stuck_warning_fired,
            artifactIds: resp.artifacts.map((a) => a.id),
          },
        ]);
        if (resp.artifacts.length > 0) {
          setArtifacts((existing) => [...existing, ...resp.artifacts]);
        }
        // Refocus input for the next message.
        setTimeout(() => inputRef.current?.focus(), 0);
      } catch (err) {
        const msg =
          err instanceof ApiError
            ? `${err.status}: ${err.message}`
            : err instanceof Error
              ? err.message
              : String(err);
        setSendError(msg);
      } finally {
        setSending(false);
      }
    },
    [session, apiClient],
  );

  // Chip click in an artifact = send that text as the next user message.
  const handleChipClick = useCallback(
    (text: string) => {
      void handleSend(text);
    },
    [handleSend],
  );

  // ── Render ────────────────────────────────────────────────────

  if (!session) {
    return (
      <section style={openShellStyle}>
        <h2 style={pageTitleStyle}>Analyst</h2>
        <p style={mutedTextStyle}>
          Start a session against a registered installation. The analyst
          works against any Lattice you can query via <code>/query</code>.
        </p>
        <div style={formRowStyle}>
          <label style={labelStyle}>
            <span>installation_id</span>
            <input
              value={installationId}
              onChange={(e) => setInstallationId(e.target.value)}
              style={inputStyle}
              placeholder="retail-demo"
              data-testid="installation-id-input"
            />
          </label>
          <label style={labelStyle}>
            <span>ac_name (optional)</span>
            <input
              value={acName}
              onChange={(e) => setAcName(e.target.value)}
              style={inputStyle}
              placeholder="leave blank when installation has one AC"
              data-testid="ac-name-input"
            />
          </label>
          <button
            type="button"
            onClick={handleOpen}
            disabled={opening}
            style={primaryButtonStyle}
            data-testid="open-session-button"
          >
            {opening ? "Opening…" : "Open session"}
          </button>
        </div>
        {openError && (
          <div style={errorBoxStyle} role="alert">
            {openError}
          </div>
        )}
      </section>
    );
  }

  return (
    <section style={twoPanelStyle}>
      <div style={dialoguePanelStyle}>
        <header style={panelHeaderStyle}>
          <strong>Dialogue</strong>
          <span style={mutedTextStyle}>
            session <code>{session.session_id}</code> · AC <code>{session.ac_name}</code>
          </span>
        </header>
        <div style={messageListStyle} data-testid="message-list">
          {dialogue.map((msg, i) => (
            <DialogueBubble key={i} msg={msg} />
          ))}
          {sending && (
            <div style={{ ...mutedTextStyle, padding: 8 }}>analyst is thinking…</div>
          )}
        </div>
        <form
          style={inputBarStyle}
          onSubmit={(e) => {
            e.preventDefault();
            void handleSend(inputText);
          }}
        >
          <textarea
            ref={inputRef}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="Ask the analyst something about your data…"
            style={textareaStyle}
            disabled={sending}
            data-testid="message-input"
            rows={2}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void handleSend(inputText);
              }
            }}
          />
          <button
            type="submit"
            disabled={sending || !inputText.trim()}
            style={primaryButtonStyle}
            data-testid="send-button"
          >
            Send
          </button>
        </form>
        {sendError && (
          <div style={errorBoxStyle} role="alert">
            {sendError}
          </div>
        )}
      </div>

      <div style={artifactPanelStyle}>
        <header style={panelHeaderStyle}>
          <strong>Artifacts</strong>
          <span style={mutedTextStyle}>
            {artifacts.length} item{artifacts.length === 1 ? "" : "s"}
          </span>
        </header>
        <div style={artifactListStyle} data-testid="artifact-list">
          {artifacts.length === 0 ? (
            <div style={emptyPanelStyle}>
              Frames, narrations, and other artifacts the analyst produces
              will appear here. Pinned items stay across session pruning.
            </div>
          ) : (
            // Newest at top — easier to scan after a turn.
            [...artifacts]
              .reverse()
              .map((a) => (
                <ArtifactCard
                  key={a.id}
                  artifact={a}
                  onChipClick={handleChipClick}
                />
              ))
          )}
        </div>
      </div>
    </section>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Dialogue bubble
// ─────────────────────────────────────────────────────────────────────


function DialogueBubble({ msg }: { msg: DialogueMessage }) {
  if (msg.role === "system") {
    return <div style={systemBubbleStyle}>{msg.text}</div>;
  }
  const isUser = msg.role === "user";
  return (
    <div style={isUser ? userBubbleStyle : assistantBubbleStyle}>
      <div style={bubbleRoleStyle}>{isUser ? "You" : "Analyst"}</div>
      <div>{msg.text}</div>
      {msg.role === "assistant" && (
        <div style={bubbleMetaStyle}>
          {msg.turns} turn{msg.turns === 1 ? "" : "s"}
          {msg.artifactIds.length > 0 && ` · ${msg.artifactIds.length} artifact(s)`}
          {msg.budgetExceeded && " · budget exceeded"}
          {msg.stuckWarning && " · stuck warning fired"}
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Inline styles
// ─────────────────────────────────────────────────────────────────────


const pageTitleStyle: React.CSSProperties = {
  margin: "0 0 8px 0",
  fontSize: 20,
};


const openShellStyle: React.CSSProperties = {
  maxWidth: 640,
  margin: "32px auto",
  padding: "0 16px",
};


const formRowStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
  marginTop: 16,
};


const labelStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  fontSize: 13,
  color: "#334155",
};


const inputStyle: React.CSSProperties = {
  border: "1px solid #cbd5e1",
  borderRadius: 4,
  padding: "6px 8px",
  fontSize: 14,
};


const primaryButtonStyle: React.CSSProperties = {
  background: "#1e40af",
  color: "#fff",
  border: "none",
  borderRadius: 4,
  padding: "8px 16px",
  cursor: "pointer",
  fontWeight: 600,
};


const twoPanelStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "minmax(320px, 1fr) minmax(420px, 1.4fr)",
  gap: 16,
  height: "calc(100vh - 100px)",
  padding: 12,
};


const dialoguePanelStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: 8,
  padding: 12,
  overflow: "hidden",
};


const artifactPanelStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  background: "#fafafa",
  border: "1px solid #e2e8f0",
  borderRadius: 8,
  padding: 12,
  overflow: "hidden",
};


const panelHeaderStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  marginBottom: 8,
};


const messageListStyle: React.CSSProperties = {
  flex: 1,
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  gap: 8,
  padding: 4,
};


const artifactListStyle: React.CSSProperties = {
  flex: 1,
  overflowY: "auto",
  padding: 4,
};


const inputBarStyle: React.CSSProperties = {
  display: "flex",
  gap: 8,
  marginTop: 8,
  alignItems: "flex-end",
};


const textareaStyle: React.CSSProperties = {
  flex: 1,
  border: "1px solid #cbd5e1",
  borderRadius: 4,
  padding: 8,
  fontSize: 14,
  resize: "vertical",
  fontFamily: "inherit",
};


const userBubbleStyle: React.CSSProperties = {
  alignSelf: "flex-end",
  maxWidth: "85%",
  background: "#dbeafe",
  border: "1px solid #93c5fd",
  borderRadius: 8,
  padding: "8px 12px",
};


const assistantBubbleStyle: React.CSSProperties = {
  alignSelf: "flex-start",
  maxWidth: "85%",
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: 8,
  padding: "8px 12px",
};


const systemBubbleStyle: React.CSSProperties = {
  alignSelf: "center",
  fontSize: 12,
  color: "#64748b",
  fontStyle: "italic",
  padding: "4px 8px",
};


const bubbleRoleStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  textTransform: "uppercase",
  color: "#64748b",
  marginBottom: 2,
};


const bubbleMetaStyle: React.CSSProperties = {
  fontSize: 11,
  color: "#94a3b8",
  marginTop: 4,
};


const mutedTextStyle: React.CSSProperties = {
  color: "#64748b",
  fontSize: 12,
};


const emptyPanelStyle: React.CSSProperties = {
  color: "#94a3b8",
  fontSize: 13,
  fontStyle: "italic",
  padding: 16,
  textAlign: "center",
};


const errorBoxStyle: React.CSSProperties = {
  background: "#fee2e2",
  border: "1px solid #fca5a5",
  color: "#991b1b",
  padding: "8px 12px",
  borderRadius: 4,
  marginTop: 8,
  fontSize: 13,
};
